import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card, Row, Col, Button, Modal, Form, Badge } from "react-bootstrap";
import { DndContext, DragOverlay, useDroppable, useDraggable } from "@dnd-kit/core";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";
import Swal from "sweetalert2";
import {
  DAYS, teacherLabel, teacherOptionLabel, subjectCode, buildGrid, shortTime,
  exportTimetablePdf, printTimetableDoc, docOptions,
} from "../utils/islamicTimetable";

const API = process.env.REACT_APP_API;

// ห้องของอีกภาค/อีกห้องที่ครูติดสอนอยู่ — ป้ายชั้นของภาคสามัญต้องมี "ม." นำหน้า
const slotRoomLabel = (slot) =>
  slot.side === "academic"
    ? `ม.${slot.class_label}/${slot.room_no}`
    : `${slot.class_label}/${slot.room_no}`;

// ─── ช่องในตาราง (รับการวาง) ─────────────────────────────────────────────────
function DroppableCell({ id, isOccupied, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const bg = isOver
    ? isOccupied ? "#ffcccc" : "#d4edda"
    : isOccupied ? "#fff" : "#fafafa";
  return (
    <div
      ref={setNodeRef}
      style={{
        minHeight: 64,
        background: bg,
        border: "1px solid #dee2e6",
        borderRadius: 4,
        padding: 2,
        transition: "background 0.12s",
      }}
    >
      {children}
    </div>
  );
}

// ─── การ์ดวิชาในแถบข้าง ──────────────────────────────────────────────────────
function SidebarCard({ subject, teacher }) {
  const assigned = subject.is_teachId ? subject : null;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `sidebar-${subject.isubject_id}`,
    data: { type: "sidebar", subject, teacher },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        transform: transform ? `translate3d(${transform.x}px,${transform.y}px,0)` : undefined,
        opacity: isDragging ? 0.35 : 1,
        cursor: "grab",
        userSelect: "none",
      }}
      className={`border rounded p-2 mb-1 ${assigned ? "border-success bg-success-subtle" : "border-secondary bg-light"}`}
    >
      <div className="fw-semibold" style={{ fontSize: "0.7rem" }}>{subjectCode(subject)}</div>
      <div className="text-muted arabic-text" style={{ fontSize: "0.75rem", lineHeight: 1.3 }}>
        {subject.isubject_name}
      </div>
      {assigned && (
        <div
          className={`text-success fw-semibold ${teacher.arabic ? "arabic-text" : ""}`}
          style={{ fontSize: "0.68rem", marginTop: 2 }}
        >
          <i className="ri-user-line me-1"></i>{teacher.name}
        </div>
      )}
    </div>
  );
}

// ─── การ์ดคาบในตาราง ─────────────────────────────────────────────────────────
function SlotCard({ slot, teacher, onDelete, isDuplicate, deleteTitle }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `slot-${slot.is_teachtime_id}`,
    data: { type: "slot", slot, teacher },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        transform: transform ? `translate3d(${transform.x}px,${transform.y}px,0)` : undefined,
        opacity: isDragging ? 0.3 : 1,
        cursor: "grab",
        fontSize: "0.72rem",
        position: "relative",
        userSelect: "none",
      }}
      className={`rounded p-1 text-white ${isDuplicate ? "bg-danger" : "bg-primary"}`}
    >
      <button
        type="button"
        className="btn-close btn-close-white position-absolute"
        style={{ top: 2, insetInlineEnd: 3, fontSize: "0.5rem", padding: "0.2rem" }}
        title={deleteTitle}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onDelete(slot.is_teachtime_id); }}
      />
      <div className="fw-semibold pe-3" style={{ lineHeight: 1.2, fontSize: "0.66rem" }}>{subjectCode(slot)}</div>
      <div className="arabic-text" style={{ lineHeight: 1.3, opacity: 0.9 }}>{slot.isubject_name}</div>
      <div className={teacher.arabic ? "arabic-text" : ""} style={{ lineHeight: 1.2, opacity: 0.75 }}>
        {teacher.name}
      </div>
    </div>
  );
}

// ─── การ์ดที่ลอยตามเมาส์ตอนลาก ───────────────────────────────────────────────
function GhostCard({ data }) {
  if (!data) return null;
  if (data.type === "sidebar") {
    const { subject, teacher } = data;
    return (
      <div className="border rounded p-2 bg-white shadow" style={{ width: 150, fontSize: "0.78rem", opacity: 0.95 }}>
        <div className="fw-semibold" style={{ fontSize: "0.7rem" }}>{subjectCode(subject)}</div>
        <div className="text-muted arabic-text" style={{ fontSize: "0.75rem" }}>{subject.isubject_name}</div>
        {teacher.name && (
          <div className={`text-success fw-semibold ${teacher.arabic ? "arabic-text" : ""}`} style={{ fontSize: "0.68rem" }}>
            {teacher.name}
          </div>
        )}
      </div>
    );
  }
  if (data.type === "slot") {
    const { slot, teacher } = data;
    return (
      <div className="rounded p-1 bg-primary text-white shadow" style={{ width: 130, fontSize: "0.72rem", opacity: 0.95 }}>
        <div className="fw-semibold" style={{ fontSize: "0.66rem" }}>{subjectCode(slot)}</div>
        <div className="arabic-text" style={{ opacity: 0.9 }}>{slot.isubject_name}</div>
        <div className={teacher.arabic ? "arabic-text" : ""} style={{ opacity: 0.75 }}>{teacher.name}</div>
      </div>
    );
  }
  return null;
}

// ─── หน้าจัดการตารางสอนภาคศาสนา ──────────────────────────────────────────────
export default function IslamicTimetableEditor() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === "th" ? th : jawi;
  const tt = t.timetableEditor;

  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearList, setYearList] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [filterClass, setFilterClass] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teacherList, setTeacherList] = useState([]);

  const [activeDrag, setActiveDrag] = useState(null);

  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [pendingDrop, setPendingDrop] = useState(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [teacherConflictInfo, setTeacherConflictInfo] = useState(null);

  const roomId = selectedRoom?.ir_id || null;
  const teacherName = useCallback((row) => teacherLabel(lang, row), [lang]);

  // ─── ข้อมูลที่แปลงมาจาก state ───────────────────────────────────────────────
  const grid = useMemo(() => buildGrid(timetable), [timetable]);

  // ─── โหลดข้อมูล ────────────────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`)
      .then((res) => {
        const list = res.data;
        setYearList(list);
        let id = localStorage.getItem("yearId");
        if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
        setYearId(id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axiosInstance.get(`${API}/getTeacherList?side=islamic`)
      .then((res) => setTeacherList(res.data))
      .catch(() => setTeacherList([]));
  }, []);

  useEffect(() => {
    if (!yearId) return;
    setSelectedRoom(null);
    setTimetable([]);
    setSubjects([]);
    axiosInstance.get(`${API}/islamic-assessment/rooms/${yearId}`)
      .then((res) => setRooms(res.data))
      .catch(() => setRooms([]));
  }, [yearId]);

  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getPeriodList/${yearId}`)
      .then((res) => setPeriods(res.data.map((p) => ({
        ...p,
        period_begin: shortTime(p.period_begin),
        period_end: shortTime(p.period_end),
      }))))
      .catch(() => setPeriods([]));
  }, [yearId]);

  const fetchTimetable = useCallback(() => {
    if (!roomId) return;
    axiosInstance.get(`${API}/islamic-teaching/timetableByRoom/${roomId}`)
      .then((res) => setTimetable(res.data))
      .catch(() => setTimetable([]));
  }, [roomId]);

  const fetchSubjects = useCallback(() => {
    if (!roomId) return;
    axiosInstance.get(`${API}/islamic-teaching/roomSubjects/${roomId}`)
      .then((res) => setSubjects(res.data))
      .catch(() => setSubjects([]));
  }, [roomId]);

  useEffect(() => { fetchTimetable(); }, [fetchTimetable]);
  useEffect(() => { fetchSubjects(); }, [fetchSubjects]);

  // ─── ตรวจครูสอนซ้อน (ดูทั้งภาคศาสนาและภาคสามัญ) ────────────────────────────
  const checkTeacherConflict = useCallback(async (t_id, day, periodId) => {
    if (!t_id || !yearId) return null;
    try {
      const { data } = await axiosInstance.get(`${API}/islamic-teaching/teacherSlots/${t_id}/${yearId}`);
      return data.find((s) =>
        Number(s.teach_day) === Number(day) &&
        Number(s.period_id) === Number(periodId) &&
        !(s.side === "islamic" && Number(s.room_id) === Number(roomId))
      ) || null;
    } catch { return null; }
  }, [yearId, roomId]);

  const confirmIfConflict = async (conflict, actionText) => {
    if (!conflict) return true;
    const result = await Swal.fire({
      icon: "warning",
      title: tt.conflictTitle,
      html: `${tt.conflictBody(slotRoomLabel(conflict))}<br>${tt.conflictAsk(actionText)}`,
      showCancelButton: true,
      confirmButtonText: actionText,
      cancelButtonText: t.common.cancel,
      confirmButtonColor: "#f57c00",
    });
    return result.isConfirmed;
  };

  // ─── การกระทำกับตาราง ──────────────────────────────────────────────────────
  const doAddTeachtime = async (is_teachId, day, periodId, t_id) => {
    const conflict = await checkTeacherConflict(t_id, day, periodId);
    if (!(await confirmIfConflict(conflict, tt.actionAdd))) return;
    setLoading(true);
    try {
      await axiosInstance.post(`${API}/islamic-teaching/addTeachtime`, {
        is_teachId, period_id: periodId, teach_day: day,
      });
      fetchTimetable();
      fetchSubjects();
    } catch (err) {
      Swal.fire({ icon: "error", title: t.common.error, text: err.response?.data?.message || tt.addFail });
    } finally { setLoading(false); }
  };

  const doMoveTeachtime = async (is_teachtime_id, day, periodId, t_id) => {
    const conflict = await checkTeacherConflict(t_id, day, periodId);
    if (!(await confirmIfConflict(conflict, tt.actionMove))) return;
    setLoading(true);
    try {
      await axiosInstance.put(`${API}/islamic-teaching/moveTeachtime`, {
        is_teachtime_id, teach_day: day, period_id: periodId,
      });
      fetchTimetable();
    } catch (err) {
      Swal.fire({ icon: "error", title: t.common.error, text: err.response?.data?.message || tt.moveFail });
    } finally { setLoading(false); }
  };

  const handleDeleteSlot = async (is_teachtime_id) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: t.manageTeaching.confirmDeleteTitle,
      text: tt.confirmDeleteSlot,
      showCancelButton: true,
      confirmButtonText: t.manageTeaching.confirmDeleteBtn,
      cancelButtonText: t.common.cancel,
      confirmButtonColor: "#dc3545",
    });
    if (!isConfirmed) return;
    setLoading(true);
    try {
      await axiosInstance.delete(`${API}/islamic-teaching/deleteTeachtime/${is_teachtime_id}`);
      fetchTimetable();
      fetchSubjects();
    } catch (err) {
      Swal.fire({ icon: "error", title: t.common.error, text: err.response?.data?.message || t.manageTeaching.deleteFailMsg });
    } finally { setLoading(false); }
  };

  // ─── ลากวาง ────────────────────────────────────────────────────────────────
  const handleDragStart = ({ active }) => setActiveDrag({ id: active.id, data: active.data.current });

  const occupiedAlert = () =>
    Swal.fire({ icon: "error", title: tt.cannotDrop, text: tt.cellOccupied, timer: 2000, showConfirmButton: false });

  const handleDragEnd = async ({ active, over }) => {
    setActiveDrag(null);
    if (!over || !String(over.id).startsWith("cell-")) return;
    const [, dayPart, periodPart] = String(over.id).split("-");
    const targetDay = parseInt(dayPart, 10);
    const targetPeriodId = parseInt(periodPart, 10);
    const activeData = active.data.current;
    const existing = grid[targetDay]?.[targetPeriodId];

    if (activeData.type === "slot") {
      const { slot } = activeData;
      if (existing && existing.is_teachtime_id === slot.is_teachtime_id) return;
      if (existing) return void occupiedAlert();
      await doMoveTeachtime(slot.is_teachtime_id, targetDay, targetPeriodId, slot.t_id);
      return;
    }

    if (activeData.type === "sidebar") {
      if (existing) return void occupiedAlert();
      const { subject } = activeData;
      if (subject.is_teachId) {
        await doAddTeachtime(subject.is_teachId, targetDay, targetPeriodId, subject.t_id);
      } else {
        setPendingDrop({ day: targetDay, periodId: targetPeriodId, subject });
        setSelectedTeacherId("");
        setTeacherConflictInfo(null);
        setShowTeacherModal(true);
      }
    }
  };

  // ─── เลือกครูตอนลากวิชาที่ยังไม่มีครู ──────────────────────────────────────
  const handleTeacherSelect = async (t_id) => {
    setSelectedTeacherId(t_id);
    if (!t_id || !pendingDrop) { setTeacherConflictInfo(null); return; }
    setTeacherConflictInfo(await checkTeacherConflict(parseInt(t_id, 10), pendingDrop.day, pendingDrop.periodId));
  };

  const handleConfirmAssign = async () => {
    if (!selectedTeacherId || !pendingDrop) return;
    const { day, periodId, subject } = pendingDrop;
    if (teacherConflictInfo && !(await confirmIfConflict(teacherConflictInfo, tt.actionAdd))) return;
    setShowTeacherModal(false);
    setLoading(true);
    try {
      await axiosInstance.post(`${API}/islamic-teaching/assignTeachingWithTime`, {
        ir_id: roomId,
        isubject_id: subject.isubject_id,
        t_id: parseInt(selectedTeacherId, 10),
        teach_day: day,
        period_id: periodId,
      });
      fetchTimetable();
      fetchSubjects();
    } catch (err) {
      Swal.fire({ icon: "error", title: t.common.error, text: err.response?.data?.message || tt.assignFail });
    } finally {
      setLoading(false);
      setPendingDrop(null);
      setSelectedTeacherId("");
      setTeacherConflictInfo(null);
    }
  };

  const handleCloseTeacherModal = () => {
    setShowTeacherModal(false);
    setPendingDrop(null);
    setSelectedTeacherId("");
    setTeacherConflictInfo(null);
  };

  // ─── พิมพ์ตารางสอน — เอกสารตามภาษาเมนู (ดู docOptions ใน utils/islamicTimetable) ──
  const printDoc = () => {
    const label = `${selectedRoom.ic_class}/${selectedRoom.ir_no}`;
    const yearLabel = yearList.find((y) => String(y.year_id) === String(yearId))?.year_year || "";
    return {
      ...docOptions(lang, t, tt),
      title: tt.docTitle(label),
      subtitle: t.common.studyYear(yearLabel),
      periods,
      cellHtml: (day, period) => {
        const slot = grid[day]?.[period.period_id];
        if (!slot) return "";
        return `<div style="font-weight:600;font-size:10px;">${subjectCode(slot)}</div>
                <div style="font-size:11px;color:#333;">${slot.isubject_name || ""}</div>
                <div style="font-size:10px;color:#555;">${teacherName(slot).name}</div>`;
      },
      filename: `${tt.docFile(label.replace("/", "-"))}.pdf`,
    };
  };

  // ─── รายการห้องหลังกรอง ────────────────────────────────────────────────────
  const classList = [...new Set(rooms.map((r) => r.ic_class))];
  const filteredRooms = rooms.filter((r) =>
    (!filterClass || r.ic_class === filterClass) &&
    (!filterGender || r.ir_gender === filterGender)
  );
  const genderLabel = (g) => (g === "m" ? t.common.male : t.common.female);

  const pendingPeriodLabel = pendingDrop
    ? `${t.days[pendingDrop.day]} · ${tt.periodNo(periods.find((p) => p.period_id === pendingDrop.periodId)?.period_order ?? "")}`
    : "";

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="main-title mb-0">{tt.pageTitle}</h4>
        <select
          className="form-select w-auto"
          value={yearId || ""}
          onChange={(e) => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }}
        >
          {yearList.map((y) => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYear(y.year_year)}</option>
          ))}
        </select>
      </div>

      <Card className="card-one">
        <Card.Header className="d-flex align-items-center flex-wrap gap-2">
          <select
            className="form-select form-select-sm w-auto"
            value={filterClass}
            onChange={(e) => { setFilterClass(e.target.value); setSelectedRoom(null); }}
          >
            <option value="">{t.manageTeaching.filterClass}</option>
            {classList.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            className="form-select form-select-sm w-auto"
            value={filterGender}
            onChange={(e) => { setFilterGender(e.target.value); setSelectedRoom(null); }}
          >
            <option value="">{t.manageTeaching.filterGender}</option>
            <option value="m">{t.common.male}</option>
            <option value="f">{t.common.female}</option>
          </select>
          <select
            className="form-select form-select-sm w-auto"
            value={roomId || ""}
            onChange={(e) => setSelectedRoom(filteredRooms.find((r) => String(r.ir_id) === e.target.value) || null)}
          >
            <option value="">{tt.roomPlaceholder}</option>
            {filteredRooms.map((r) => (
              <option key={r.ir_id} value={r.ir_id}>
                {r.ic_class}/{r.ir_no} ({r.is_programCode})
              </option>
            ))}
          </select>
          {selectedRoom && (
            <Badge bg={selectedRoom.ir_gender === "m" ? "primary" : "danger"}>
              {genderLabel(selectedRoom.ir_gender)}
            </Badge>
          )}
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Button
              variant="outline-danger"
              size="sm"
              disabled={!roomId || periods.length === 0}
              onClick={() => exportTimetablePdf(printDoc())}
            >
              <i className="ri-download-2-line me-1"></i>{tt.exportPdf}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!roomId || periods.length === 0}
              onClick={() => printTimetableDoc(printDoc())}
            >
              <i className="ri-printer-line me-1"></i>{tt.print}
            </Button>
          </div>
        </Card.Header>

        {!roomId ? (
          <Card.Body>
            <div className="text-center text-secondary py-5">{t.manageTeaching.selectRoom}</div>
          </Card.Body>
        ) : (
          <Card.Body className="p-2">
            <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <Row className="g-2">
                {/* แถบข้าง — รายวิชาในหลักสูตรของห้อง */}
                <Col xs={12} md={3} lg={2} style={{ maxHeight: "72vh", overflowY: "auto" }}>
                  <div className="fw-semibold mb-2 text-muted" style={{ fontSize: "0.8rem" }}>
                    {tt.subjectsTitle}
                    <span className="text-secondary fw-normal"> {tt.dragHint}</span>
                  </div>
                  {subjects.length === 0
                    ? <div className="text-muted fs-sm">{tt.noSubjects}</div>
                    : subjects.map((s) => (
                        <SidebarCard key={s.isubject_id} subject={s} teacher={teacherName(s)} />
                      ))}
                </Col>

                {/* ตาราง */}
                <Col xs={12} md={9} lg={10} style={{ overflowX: "auto" }}>
                  {periods.length === 0 ? (
                    <div className="text-center text-muted py-5">{tt.noPeriods}</div>
                  ) : (
                    <table
                      className="table table-bordered mb-0"
                      style={{ tableLayout: "fixed", minWidth: periods.length * 100 + 80 }}
                    >
                      <thead className="table-secondary">
                        <tr>
                          <th style={{ width: 80, fontSize: "0.78rem" }}>{tt.colDay}</th>
                          {periods.map((p) => (
                            <th key={p.period_id} className="text-center" style={{ fontSize: "0.73rem" }}>
                              <div className="fw-semibold">{tt.periodNo(p.period_order)}</div>
                              <div className="text-muted fw-normal">{p.period_begin}–{p.period_end}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {DAYS.map((d) => (
                          <tr key={d}>
                            <td className="text-center align-middle fw-semibold" style={{ fontSize: "0.78rem" }}>
                              {t.days[d]}
                            </td>
                            {periods.map((p) => {
                              const slot = grid[d]?.[p.period_id];
                              return (
                                <td key={p.period_id} style={{ padding: 2 }}>
                                  <DroppableCell id={`cell-${d}-${p.period_id}`} isOccupied={!!slot}>
                                    {slot && (
                                      <SlotCard
                                        slot={slot}
                                        teacher={teacherName(slot)}
                                        onDelete={handleDeleteSlot}
                                        isDuplicate={!!slot.has_teacher_conflict}
                                        deleteTitle={tt.deleteSlotTitle}
                                      />
                                    )}
                                  </DroppableCell>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Col>
              </Row>

              <DragOverlay dropAnimation={null}>
                {activeDrag && <GhostCard data={activeDrag.data} />}
              </DragOverlay>
            </DndContext>
          </Card.Body>
        )}
      </Card>

      {/* เลือกครูผู้สอนเมื่อลากวิชาที่ยังไม่มีครู */}
      <Modal show={showTeacherModal} onHide={handleCloseTeacherModal}>
        <Modal.Header closeButton>
          <Modal.Title>{tt.teacherModalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingDrop && (
            <div className="mb-3 p-2 bg-light rounded" style={{ fontSize: "0.85rem" }}>
              <div>
                <strong>{t.manageTeaching.labelSubject}:</strong>{" "}
                {subjectCode(pendingDrop.subject)} — <span className="arabic-text">{pendingDrop.subject.isubject_name}</span>
              </div>
              <div><strong>{tt.labelSlot}:</strong> {pendingPeriodLabel}</div>
            </div>
          )}
          <Form.Select value={selectedTeacherId} onChange={(e) => handleTeacherSelect(e.target.value)}>
            <option value="">{t.manageTeaching.teacherPlaceholder}</option>
            {teacherList.map((tc) => (
              <option key={tc.t_id} value={tc.t_id}>{teacherOptionLabel(lang, tc)}</option>
            ))}
          </Form.Select>
          {teacherConflictInfo && (
            <div className="alert alert-warning mt-2 py-1 mb-0" style={{ fontSize: "0.82rem" }}>
              <i className="ri-alert-line me-1"></i>
              {tt.conflictBody(slotRoomLabel(teacherConflictInfo))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseTeacherModal}>{t.common.cancel}</Button>
          <Button variant="success" onClick={handleConfirmAssign} disabled={!selectedTeacherId}>
            {t.common.save}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
